import { InjectProperty } from '@jack-henry/frontend-utils/di';
import type { CompanyAccountModelDto } from '@treasury/api/channel';
import { AchException } from '@treasury/domain/arp';
import { TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/layout/tm-section';
import '@treasury/presentation/components/tm-badge';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-footer';
import '@treasury/presentation/components/tm-list-header';
// eslint-disable-next-line no-duplicate-imports
import { TmSearchTermChangeEvent } from '@treasury/presentation/components/tm-list-header';
import { columnBodyRenderer } from '@vaadin/grid/lit.js';
import '@vaadin/grid/vaadin-grid-selection-column.js';
import '@vaadin/grid/vaadin-grid-sort-column.js';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { filterTableResults } from '../../../../utilities/table-result-filter';
import AchExceptionsActivityDataProvider from '../data/ach-exceptions-activity-data-provider';
import { AchExceptionActivityVm } from '../view-models/ach-exception-activity';
import './ach-exceptions-activity-filter-sheet';

export const tagName = 'ach-exceptions-activity-table';
@customElement(tagName)
export class AchExceptionsActivityTable extends TmBaseComponent {
    @property({ type: Array })
    public achExceptions: AchException[] = [];

    @property({ type: Array })
    public filteredItems: AchException[] = [];

    @property({ type: Boolean })
    public readonly = false;

    @state()
    private loading = true;

    @property({ type: Array })
    private accounts: CompanyAccountModelDto[] = [];

    @property({ type: Array })
    private secCodes: { secCode?: string; description?: string }[] = [];

    @state()
    private filterSheetOpen = false;

    @state()
    private fieldsToFilter: string[] = [
        'decisionBy',
        'decisionDate',
        'decision',
        'amount',
        'achCompanyName',
    ];

    @InjectProperty()
    declare private dataProvider: AchExceptionsActivityDataProvider;

    public render() {
        return html`<tm-section class="border-b">
                ${this.renderTableHeader()}${this.renderTable()} </tm-section
            >${this.renderFilterSheet()}`;
    }

    protected updated(changedProperties: Map<string | number | symbol, unknown>): void {
        super.firstUpdated(changedProperties);
        if (changedProperties.has('loading')) {
            this.filteredItems = this.achExceptions;
        }
    }

    private renderDecisionColumn(achException: AchExceptionActivityVm) {
        return html`<div class="flex flex-col">
            <span class="text-sm font-medium ml-2 pr-2">${achException.decisionBy}</span>
            <span class="text-sm ml-2 pr-2">${achException.decisionDate}</span>
        </div>`;
    }

    private renderAmountColumn(achException: AchExceptionActivityVm) {
        return html`<div class="flex flex-col">
            <span class="text-sm font-medium mr-2 pr-2">${achException.amount}</span>
            <span class="text-sm mr-2 pr-2">${achException.achCompanyName}</span>
        </div>`;
    }

    private renderColumns() {
        return html`<vaadin-grid-column
                ${columnBodyRenderer(this.renderDecisionColumn, [])}
            ></vaadin-grid-column
            ><vaadin-grid-column
                text-align="end"
                ${columnBodyRenderer(this.renderAmountColumn, [])}
            ></vaadin-grid-column>`;
    }

    private renderTable() {
        if (this.loading) {
            return html`<div class="text-center empty-state py-16">
                <tm-loader card></tm-loader>
            </div>`;
        }

        if (!this.achExceptions.length) {
            return html`<div class="w-full px-4 py-8 text-center text-sm text-secondary">
                No decisions found.
            </div> `;
        }

        return html`<vaadin-grid .items=${this.filteredItems} .dataProvider=${this.dataProvider}
            >${this.renderColumns()}</vaadin-grid
        >`;
    }

    private renderTableHeader() {
        return html`<tm-list-header
            .headerTitle=${'ACH Exception Decisions'}
            .filteringEnabled=${!this.readonly}
            .filterButton=${this.accounts.length && this.secCodes.length}
            @filterButtonClick=${() => (this.filterSheetOpen = true)}
            @search-term-change=${(e: TmSearchTermChangeEvent) => {
                this.filteredItems = filterTableResults(e, this.achExceptions, this.fieldsToFilter);
            }}
        ></tm-list-header>`;
    }

    private renderFilterSheet() {
        if (!this.accounts.length || !this.secCodes.length) {
            return nothing;
        }
        return html`<ach-exceptions-activity-filter-sheet
            .open=${this.filterSheetOpen}
            .accounts=${this.accounts}
            .secCodes=${this.secCodes}
            @close=${() => {
                this.filterSheetOpen = false;
            }}
        ></ach-exceptions-activity-filter-sheet>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    --lumo-space-xs: 3px;
                    --lumo-space-m: 0;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                }
                .empty-state {
                    color: var(--header-text-color);
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AchExceptionsActivityTable;
    }
}
